import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Label, Row } from "reactstrap";
import Select from "../../Common/AppSelect";
import LoadingSpinner from "../../Common/Spinner";
import { isEmpty } from "lodash";
import { getCustomerCards } from "../../../store/charges/action";
import {
  confirmSubscriptionLicensesOrder,
  confirmSubscriptionLicensesOrderSuccess,
} from "../../../store/manageLicense/action";
import { toast } from "react-toastify";
import {
  createStaffAndUser,
  createStaffAndUserSuccess,
} from "../../../store/staff/action";
import Staff from "../../../store/staff/reducer";
import {
  companyToUserUpdateSubscriptionLicenceAndSecurity,
  companyToUserUpdateSubscriptionLicenceAndSecuritySuccess,
  getCompanyToUserListTeam,
} from "../../../store/companyToUser/action";
import { Tooltip } from "@mui/material";
import { useBranding } from "../../branding/Branding";
import AddCustomerCard from "../AddCustomerCard/AddCustomerCard";
// Change #100462
import { getSubscriptionProductList } from "../../../store/manageLicense/action";
import EnterpriseSeats, {
  useSubscriptionBilling,
  isEnterpriseBilling,
  enterpriseCanAdd,
} from "./EnterpriseSeats";

const TabSummary = ({
  activeVerticalTab,
  setActiveVerticalTab,
  createUpdateData,
  setCreateUpdateData,
  setModal,
  isEdit,
  // Change #100163 (revised) - hands the created user to the wizard so its
  // Working Schedule step knows whose schedule to edit.
  onUserCreated,
}) => {
  const dispatch = useDispatch();
  const { isBrandingActive } = useBranding();

  const { userAvailableDashboardAPI } = useSelector((state) => ({
    userAvailableDashboardAPI:
      state.CompanyToUser.userAvailableDashboard?.dashboardtemplate_list,
  }));
  const { menuNavigationListAPI } = useSelector((state) => ({
    menuNavigationListAPI:
      state.NavigationMenu.menuNavigationList?.menunav_list,
  }));
  const { availableMenuNavigationItemsAPI } = useSelector((state) => ({
    // Task 24678 - MenuNavOption (master), NOT MenuNavItem (per company).
    // They are different tables with different id sequences, and
    // AuthLoginController resolves Staff.DefaultOpeningNavMenu against
    // MenuNavOption.Id - so a MenuNavItem id saved here points at whichever
    // MenuNavOption happens to share the number, or at nothing.
    availableMenuNavigationItemsAPI:
      state.NavigationMenu.availableMenuNavigationItems?.menunavoption_list,
  }));
  const { securityAuthRoleListAPI } = useSelector((state) => ({
    securityAuthRoleListAPI:
      state.Security.securityAuthRoleList?.securityauthrole_list,
  }));
  const { getLicensesSubscriptionProductListAPI } = useSelector((state) => ({
    getLicensesSubscriptionProductListAPI:
      state.ManageLicense.subscriptionProductList?.subscriptionproduct_list,
  }));

  // Change #100462 - Enterprise: no amount, no card, just the seats.
  const billing = useSubscriptionBilling();
  const isEnterprise = isEnterpriseBilling(billing);

  const [modalAddCustomerCard, setModalAddCustomerCard] = useState(false);
  const toggleAddCustomerCard = () =>
    setModalAddCustomerCard(!modalAddCustomerCard);
  const [subscriptionCompleted, setSubscriptionCompleted] = useState(false);
  const [
    subscriptionCompletedResponseData,
    setSubscriptionCompletedResponseData,
  ] = useState([]);

  const { CustomerCardsList } = useSelector((state) => ({
    CustomerCardsList: state.Charges.customerCardsList?.masterpaymentcard_list,
  }));
  useEffect(() => {
    if (isEmpty(CustomerCardsList)) return;
    const findPrimaryCard = CustomerCardsList?.filter(
      (item) => item.FlagPrimary === "1"
    );
    if (isEmpty(findPrimaryCard)) return;
    setCreateUpdateData({
      ...createUpdateData,
      MasterPaymentCard_Id: findPrimaryCard[0].Id,
    });
  }, [CustomerCardsList]);

  useEffect(() => {
    dispatch(getCustomerCards());
    return () => {
      setSubscriptionCompletedResponseData([]);
      setSubscriptionCompleted(false);
    };
  }, []);

  const {
    confirmSubscriptionLicensesOrderSuccessAPI,
    confirmSubscriptionLicensesOrderLoading,
    companyToUserUpdateSubscriptionLicenceAndSecurityAPI,
    companyToUserUpdateSubscriptionLicenceAndSecurityLoading,
  } = useSelector((state) => ({
    confirmSubscriptionLicensesOrderSuccessAPI:
      state.Staff.createStaffAndUserSuccess,
    confirmSubscriptionLicensesOrderLoading:
      state.Staff.loadingCreateStaffAndUser,
    companyToUserUpdateSubscriptionLicenceAndSecurityAPI:
      state.CompanyToUser.companyToUserUpdateSubscriptionLicenceAndSecurity,
    companyToUserUpdateSubscriptionLicenceAndSecurityLoading:
      state.CompanyToUser
        .companyToUserUpdateSubscriptionLicenceAndSecurityLoading,
  }));

  const handleConfirmLicensesOrder = () => {
    // need to upgrade to a merged data of both card and license
    dispatch(createStaffAndUser([createUpdateData]));
  };

  const handleUpdateUserAndOrderLicenses = () => {
    dispatch(
      companyToUserUpdateSubscriptionLicenceAndSecurity([createUpdateData])
    );
  };

  // Start another user keeping the same settings: wipe only the
  // person-specific fields and send the wizard back to Step 1.
  const handleCreateAnotherUser = () => {
    setCreateUpdateData({
      ...createUpdateData,
      User_Email: "",
      User_UserFirstName: "",
      User_UserLastName: "",
      User_ImageUrl: "",
      // Task 24455 - phone numbers and a contact address belong to one person,
      // so they must not carry over. Job Title deliberately DOES carry: adding
      // three technicians in a row is exactly what "same settings" is for.
      Staff_ContactTelephoneNumber: "",
      Staff_ContactMobileNumber: "",
      Staff_ContactEmailAddress: "",
    });
    setUserInformation({});
    if (typeof onUserCreated === "function") onUserCreated(null);
    setActiveVerticalTab(1);
  };

  const [userInformation, setUserInformation] = useState({});

  useEffect(() => {
    setUserInformation({});
  }, []);

  //create
  useEffect(() => {
    if (isEmpty(confirmSubscriptionLicensesOrderSuccessAPI)) return;
    if (confirmSubscriptionLicensesOrderSuccessAPI.status === 200) {
      toast.success(confirmSubscriptionLicensesOrderSuccessAPI?.message);
      // Change #100462 - over the agreed seats (inside the tolerance), and
      // refresh the seat count for "create another".
      if (confirmSubscriptionLicensesOrderSuccessAPI?.warning) {
        toast.warning(confirmSubscriptionLicensesOrderSuccessAPI.warning, { autoClose: 10000 });
      }
      if (isEnterprise) dispatch(getSubscriptionProductList());
      if (!isEmpty(confirmSubscriptionLicensesOrderSuccessAPI?.users[0])) {
        const created = confirmSubscriptionLicensesOrderSuccessAPI.users[0];
        setUserInformation(created);
        // Change #100163 (revised) - only a brand-new user gets the schedule
        // step; an existing platform user attached to the company already has
        // a profile to set it on.
        if (typeof onUserCreated === "function") {
          onUserCreated(
            created?.FlagUserCreated !== "0" && created?.Staff_Id
              ? {
                  ...created,
                  FirstName: createUpdateData?.User_UserFirstName,
                  LastName: createUpdateData?.User_UserLastName,
                }
              : null
          );
        }
      }
      dispatch(getCompanyToUserListTeam());
    } else {
      toast.error(confirmSubscriptionLicensesOrderSuccessAPI.message);
    }
    dispatch(createStaffAndUserSuccess([]));
  }, [confirmSubscriptionLicensesOrderSuccessAPI]);

  //update
  useEffect(() => {
    if (isEmpty(companyToUserUpdateSubscriptionLicenceAndSecurityAPI)) return;
    if (companyToUserUpdateSubscriptionLicenceAndSecurityAPI.status === 200) {
      toast.success(
        companyToUserUpdateSubscriptionLicenceAndSecurityAPI.message
      );
      // setUserInformation(
      //   companyToUserUpdateSubscriptionLicenceAndSecurityAPI.users[0]
      // );
      dispatch(getCompanyToUserListTeam());
      setModal(false);
    } else {
      toast.error(companyToUserUpdateSubscriptionLicenceAndSecurityAPI.message);
    }
    dispatch(companyToUserUpdateSubscriptionLicenceAndSecuritySuccess([]));
  }, [companyToUserUpdateSubscriptionLicenceAndSecurityAPI]);

  const [copyToClipboard, setCopyToClipboard] = useState("");

  useEffect(() => {
    const delay = setTimeout(() => {
      setCopyToClipboard(null);
    }, 1000);
    return () => {
      clearTimeout(delay);
    };
  }, [copyToClipboard]);
  return (
    <div>
      <div>
        <h2>User Summary</h2>
      </div>
      <div className={"mt-2"}>
        <hr />
        <Row>
          {isEmpty(userInformation) && (
            <Col lg={4}>
              <h6>User Details</h6>
              <div>First and Last Name</div>
              <div>
                {createUpdateData?.User_ImageUrl && (
                  <img
                    src={createUpdateData?.User_ImageUrl}
                    alt=""
                    className="img-fluid rounded-circle avatar-xxs"
                  />
                )}

                <span>
                  {" "}
                  {createUpdateData?.User_UserFirstName +
                    " " +
                    createUpdateData?.User_UserLastName}
                </span>
              </div>
              <div>
                <div className={"mt-2"}>
                  <Label className={"fw-bold"}>Email</Label>
                </div>
                <div className={" d-flex align-items-center"}>
                  <span style={{ width: "35px", textAlign: "center" }}>
                    <i
                      style={{ fontSize: "20px" }}
                      className="fa-regular fa-envelope me-2"
                    ></i>
                  </span>
                  {createUpdateData?.User_Email}
                </div>
              </div>

              {/* Task 24455 - the company-local half of the new record. */}
              {(createUpdateData?.Staff_JobTitle ||
                createUpdateData?.Staff_ContactTelephoneNumber ||
                createUpdateData?.Staff_ContactMobileNumber ||
                createUpdateData?.Staff_ContactEmailAddress) && (
                <div className={"mt-3"}>
                  <h6>Staff Details</h6>
                  {createUpdateData?.Staff_JobTitle && (
                    <div>
                      <Label className={"fw-bold mb-0"}>Job Title</Label>
                      <div>{createUpdateData.Staff_JobTitle}</div>
                    </div>
                  )}
                  {createUpdateData?.Staff_ContactTelephoneNumber && (
                    <div className={"mt-1"}>
                      <Label className={"fw-bold mb-0"}>Telephone</Label>
                      <div>{createUpdateData.Staff_ContactTelephoneNumber}</div>
                    </div>
                  )}
                  {createUpdateData?.Staff_ContactMobileNumber && (
                    <div className={"mt-1"}>
                      <Label className={"fw-bold mb-0"}>Mobile</Label>
                      <div>{createUpdateData.Staff_ContactMobileNumber}</div>
                    </div>
                  )}
                  {createUpdateData?.Staff_ContactEmailAddress && (
                    <div className={"mt-1"}>
                      <Label className={"fw-bold mb-0"}>Contact Email</Label>
                      <div>{createUpdateData.Staff_ContactEmailAddress}</div>
                    </div>
                  )}
                </div>
              )}
            </Col>
          )}

          <Col lg={isEmpty(userInformation) ? 4 : 6}>
            <h6>Default Options</h6>
            <Label className={"fw-bold my-1"}>Default Dashboard</Label>
            <div className={"d-flex align-items-center"}>
              <span style={{ width: "35px", textAlign: "center" }}>
                <i
                  style={{ fontSize: "20px" }}
                  className="fa-solid fa-table-columns me-2"
                ></i>
              </span>
              {
                userAvailableDashboardAPI?.find(
                  (item) => item?.Id === createUpdateData?.DashboardTemplate_Id
                )?.Name
              }
            </div>
            <Label className={"fw-bold my-1"}>Navigation Menu</Label>
            <div className={"mt-2 d-flex align-items-center"}>
              <span style={{ width: "35px", textAlign: "center" }}>
                <i
                  style={{ fontSize: "20px" }}
                  className="fa-solid fa-bars me-2"
                ></i>
              </span>
              {
                menuNavigationListAPI?.find(
                  (item) => item?.Id === createUpdateData?.MenuNav_Id
                )?.Name
              }
            </div>
            <Label className={"fw-bold my-1"}>Default Landing Page</Label>
            <div className={"mt-2 d-flex align-items-center"}>
              <span style={{ width: "35px", textAlign: "center" }}>
                <i
                  style={{ fontSize: "20px" }}
                  className="fa-solid fa-file me-2"
                ></i>
              </span>

              {
                availableMenuNavigationItemsAPI?.find(
                  (item) => item?.Id === createUpdateData?.DefaultOpeningNavMenu
                )?.Name
              }
            </div>
            <Label className={"fw-bold my-1"}>Security Roles</Label>
            <div className={"mt-2 d-flex align-items-center"}>
              <span style={{ width: "35px", textAlign: "center" }}>
                <i
                  style={{ fontSize: "20px" }}
                  className="fa-solid fa-user-shield me-2"
                ></i>
              </span>

              {createUpdateData?.securityauthrole_list?.map((item) => {
                return (
                  securityAuthRoleListAPI?.find((role) => role?.Id === item?.Id)
                    ?.Name + ", "
                );
              })}
            </div>
          </Col>
          <Col lg={isEmpty(userInformation) ? 4 : 6}>
            <h6>Subscription Plans</h6>
            <div>
              {createUpdateData?.subscriptionproduct_list?.map(
                (item, index) => {
                  return (
                    <div key={index}>
                      {" "}
                      <div className={"d-flex align-items-center"}>
                        <span style={{ width: "35px", textAlign: "center" }}>
                          <i
                            style={{ fontSize: "20px" }}
                            className="fa-solid fa-calendar-check me-2"
                          ></i>
                        </span>
                        {getLicensesSubscriptionProductListAPI?.find(
                          (role) => role?.Id === item?.Id
                        )?.Name +
                          "  £" +
                          getLicensesSubscriptionProductListAPI?.find(
                            (role) => role?.Id === item?.Id
                          )?.AmountLocalTotal +
                          " INC.VAT " +
                          (getLicensesSubscriptionProductListAPI?.find(
                            (role) => role?.Id === item?.Id
                          )?.QtyLicenceAvailable > 0
                            ? "(" +
                              getLicensesSubscriptionProductListAPI?.find(
                                (role) => role?.Id === item?.Id
                              )?.QtyLicenceAvailable +
                              " Licenses Available)"
                            : "")}
                      </div>
                    </div>
                  );
                }
              )}
            </div>
          </Col>

          {!isEmpty(userInformation) ? (
            <Col lg={12}>
              <hr />
              {userInformation?.FlagUserCreated === "0" ? (
                <div className={"mt-3"}>
                  <h2>
                    This user already has a{" "}
                    {isBrandingActive && "CRMit - "} account and has been
                    successfully assigned to your company.
                  </h2>
                </div>
              ) : (
                <div className={"mt-3"}>
                  <h2>Thank you User Created</h2>
                  <div>
                    User Name:
                    <span
                      onClick={() => {
                        navigator.clipboard.writeText(userInformation?.Email);
                        setCopyToClipboard(userInformation?.Email);
                      }}
                      className={
                        (copyToClipboard === userInformation?.Email
                          ? "ms-1 text-success"
                          : "ms-1 text-muted cursor-pointer") +
                        " d-flex align-items-center"
                      }
                    >
                      <Tooltip title="Click to copy" placement="top" arrow>
                        <span className="badge bg-light text-dark fw-bolder">
                          {userInformation?.Email}
                        </span>
                        <i className="ms-1 fa-solid fa-copy"></i>
                        {copyToClipboard === userInformation?.Email && (
                          <span className={"fs-10 ms-2"}>Copied!</span>
                        )}
                      </Tooltip>
                    </span>
                  </div>
                  {userInformation?.Password && (
                    <div>
                      User Password:
                      <span
                        onClick={() => {
                          navigator.clipboard.writeText(
                            userInformation?.Password
                          );
                          setCopyToClipboard(userInformation?.Password);
                        }}
                        className={
                          (copyToClipboard === userInformation?.Password
                            ? "ms-1 text-success"
                            : "ms-1 text-muted cursor-pointer") +
                          " d-flex align-items-center"
                        }
                      >
                        <Tooltip title="Click to copy" placement="top" arrow>
                          <span className="badge bg-light text-dark fw-bolder">
                            {userInformation?.Password}
                          </span>
                          <i className="ms-1 fa-solid fa-copy"></i>
                          {copyToClipboard === userInformation?.Password && (
                            <span className={"fs-10 ms-2"}>Copied!</span>
                          )}
                        </Tooltip>
                      </span>
                    </div>
                  )}

                  {/* Change #100163 (revised) - the working schedule is
                      now Step 6 of its own; this is the way into it. */}
                  <div className={"d-flex justify-content-end flex-wrap gap-2 mt-3"}>
                    {userInformation?.Staff_Id && (
                      <span
                        className={"btn btn-primary"}
                        onClick={() => setActiveVerticalTab(6)}
                      >
                        Next: Working schedule
                        <i className="ri-arrow-right-line ms-1"></i>
                      </span>
                    )}
                    <span
                      className={"btn btn-success me-2"}
                      onClick={handleCreateAnotherUser}
                    >
                      Create Another User (Same Settings)
                    </span>
                    <span
                      className={"btn btn-danger"}
                      onClick={() => {
                        setModal(false);
                      }}
                    >
                      Close
                    </span>
                  </div>
                </div>
              )}
            </Col>
          ) : isEnterprise && !isEdit ? (
            // Change #100462 - Enterprise: the seat count instead of an
            // amount and a card. Nothing is charged.
            <Col lg={12}>
              <Row className={"mt-3"}>
                <hr />
                <Col lg={12}>
                  <EnterpriseSeats billing={billing} adding={1} />
                </Col>
                <Col lg={12} className={"mt-3"}>
                  {confirmSubscriptionLicensesOrderLoading ? (
                    <LoadingSpinner
                      size={"small"}
                      row={true}
                      title={"Please wait..."}
                      extraText={"Setting up the new user"}
                    />
                  ) : (
                    <button
                      type="button"
                      className={"btn btn-success"}
                      disabled={!enterpriseCanAdd(billing)}
                      onClick={handleConfirmLicensesOrder}
                    >
                      Create New User
                    </button>
                  )}
                </Col>
              </Row>
            </Col>
          ) : (
            <Col lg={12}>
              <Row className={"mt-3"}>
                <hr />
                <Col lg={4}>
                  <span className={"fs-16 d-flex align-items-end flex-column"}>
                    Amount due today:
                  </span>
                  <div
                    // style={{ width: "200px" }}
                    className={"d-flex align-items-end flex-column"}
                  >
                    <div>
                      <span>
                        Subtotal: £ {createUpdateData?.AmountLocalSubTotal}
                      </span>
                    </div>
                    <div>
                      <span>VAT: £ {createUpdateData?.AmountLocalVat}</span>
                    </div>
                    <div>
                      <span>Total: £ {createUpdateData?.AmountLocalTotal}</span>
                    </div>
                  </div>
                </Col>
                <Col lg={8}>
                  <div>
                    <div>
                      <span>Select Preferred card or add New one</span>
                    </div>
                    <div className={"d-flex flex-row"}>
                      <Select
                        className={"w-100 pe-2"}
                        isClearable={true}
                        value={
                          createUpdateData?.MasterPaymentCard_Id
                            ? {
                                value: createUpdateData?.MasterPaymentCard_Id,
                                label:
                                  CustomerCardsList?.find(
                                    (card) =>
                                      card?.Id ===
                                      createUpdateData?.MasterPaymentCard_Id
                                  )?.Brand +
                                  "Expiry " +
                                  CustomerCardsList?.find(
                                    (card) =>
                                      card?.Id ===
                                      createUpdateData?.MasterPaymentCard_Id
                                  )?.ExpiryMonth +
                                  " / " +
                                  CustomerCardsList?.find(
                                    (card) =>
                                      card?.Id ===
                                      createUpdateData?.MasterPaymentCard_Id
                                  )?.ExpiryYear +
                                  " last 4 digits ..." +
                                  CustomerCardsList?.find(
                                    (card) =>
                                      card?.Id ===
                                      createUpdateData?.MasterPaymentCard_Id
                                  )?.Last4Digits,
                              }
                            : []
                        }
                        options={CustomerCardsList?.map((card) => ({
                          value: card.Id,
                          label: `${card?.Brand} Expiry [${card?.ExpiryMonth} / ${card?.ExpiryYear}] last 4 digits ...${card?.Last4Digits}`,
                        }))}
                        onChange={(e) => {
                          setCreateUpdateData({
                            ...createUpdateData,
                            MasterPaymentCard_Id: e.value,
                          });
                        }}
                      ></Select>
                      <span
                        className={"btn btn-primary"}
                        style={{ width: "150px" }}
                        onClick={toggleAddCustomerCard}
                      >
                        {" "}
                        Add New Card
                      </span>
                    </div>
                  </div>
                  <div className={"mt-2"}>
                    {confirmSubscriptionLicensesOrderLoading ? (
                      <div style={{}}>
                        <LoadingSpinner
                          size={"small"}
                          row={true}
                          title={"Please wait..."}
                          extraText={"Processing and setting up new User"}
                        />
                      </div>
                    ) : (
                      <span
                        className={"btn btn-success"}
                        onClick={() => {
                          isEdit
                            ? handleUpdateUserAndOrderLicenses()
                            : handleConfirmLicensesOrder();
                        }}
                      >
                        {isEdit
                          ? "Update User"
                          : "Create New user and Order Licenses"}
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          )}
        </Row>
      </div>
      {modalAddCustomerCard && (
        <AddCustomerCard
          modalAddCustomerCard={modalAddCustomerCard}
          setModalAddCustomerCard={setModalAddCustomerCard}
          toggleAddCustomerCard={toggleAddCustomerCard}
        />
      )}
    </div>
  );
};

export default TabSummary;
